import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  ENavigationItemRestriction,
  ENavigationItemStructure,
  EPageRestriction,
} from '@/graphql-types/globalTypes';
import { useWebflowPoc } from '@/hooks/useWebflowPoc';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Semibold } from '@/imports/ui/chakra/components/Semibold';
import { BackButton } from '@/modules/website-builder/components/BackButton';
import { SearchablePageSelect } from '@/modules/website-builder/components/SearchablePageSelect';
import { NavigationItem } from '@/modules/website-builder/types';
import {
  PAGE_RESTRICTION_TO_NAVIGATION_ITEM_RESTRICTION,
  READABLE_NAVIGATION_ITEM_RESTRICTION,
  READABLE_PAGE_RESTRICTION,
} from '@/utilities';

type NewDirectLinkFormValues = {
  label: string;
  href: string | null;
  page: {
    _id: string;
    name: string;
    restriction: EPageRestriction;
  } | null;
  restriction: ENavigationItemRestriction;
  /**
   * CMS: created-page → page picker; link → free-text href.
   * Webflow: created-page → Webflow path (/site/…); link → external URL.
   * Both Webflow options store href (no CMS page document).
   */
  directLinkType: 'created-page' | 'link';
};

const isExternalHttpUrl = (value: string | null | undefined): boolean =>
  !!value && /^https?:\/\//i.test(value.trim());

export const NewDirectLinkForm: React.FC<{
  onClickBack: () => void;
  onSubmit: (item: NavigationItem) => void;
  backText: string;
  submitText: string;
}> = (props) => {
  const webflowPoc = useWebflowPoc();
  const isWebflowEnabled = webflowPoc.isEnabled;

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    watch,
    setValue,
    getValues,
    control,
  } = useForm<NewDirectLinkFormValues>({
    defaultValues: {
      label: '',
      href: null,
      page: null,
      restriction: ENavigationItemRestriction.ANYONE,
      directLinkType: 'created-page',
    },
  });

  const directLinkType = watch('directLinkType');
  const page = watch('page');

  const onSubmit = handleSubmit((data) => {
    if (isWebflowEnabled) {
      // Webflow tenants have no CMS pages — both toggle options persist href.
      props.onSubmit({
        label: data.label,
        href: data.href,
        page: null,
        restriction: data.restriction,
        defaultItemIdentifier: null,
        children: [],
        structure: ENavigationItemStructure.DIRECT_LINK,
      });
      return;
    }

    props.onSubmit({
      label: data.label,
      href: data.directLinkType === 'link' ? data.href : null,
      page: data.directLinkType === 'created-page' ? data.page : null,
      restriction: data.restriction,
      defaultItemIdentifier: null,
      children: [],
      structure: ENavigationItemStructure.DIRECT_LINK,
    });
  });

  React.useEffect(() => {
    if (page && !isWebflowEnabled) {
      setValue(
        'restriction',
        PAGE_RESTRICTION_TO_NAVIGATION_ITEM_RESTRICTION[page.restriction]
      );
    }
  }, [isWebflowEnabled, page, setValue]);

  React.useEffect(() => {
    if (!touchedFields.label && !isWebflowEnabled) {
      if (directLinkType === 'created-page') {
        if (page) {
          setValue('label', page.name);
        }
      } else {
        setValue('label', '');
      }
    }
  }, [
    directLinkType,
    getValues,
    isWebflowEnabled,
    page,
    setValue,
    touchedFields.label,
  ]);

  const setDirectLinkType = (type: 'created-page' | 'link') => {
    setValue('directLinkType', type);
    if (isWebflowEnabled) {
      setValue('page', null);
      setValue('href', null);
    } else if (type === 'link') {
      setValue('page', null);
    } else {
      setValue('href', null);
    }
  };

  return (
    <Stack spacing="4">
      <BackButton onClick={props.onClickBack}>{props.backText}</BackButton>

      <Card border="1px" borderColor="gray.200" shadow="sm" p="6">
        <FormControl isInvalid={!!errors.label}>
          <FormLabel>Tab Name</FormLabel>
          <Input {...register('label', { required: 'Tab name is required' })} />
          <FormErrorMessage>{errors.label?.message}</FormErrorMessage>
        </FormControl>
      </Card>

      <Card border="1px" borderColor="gray.200" shadow="sm" p="6">
        <Stack spacing="4">
          <ButtonGroup isAttached>
            <Button
              size="sm"
              variant={
                directLinkType === 'created-page' ? undefined : 'outline'
              }
              onClick={() => setDirectLinkType('created-page')}
              colorScheme={
                directLinkType === 'created-page' ? 'purple' : 'gray'
              }
              w="full"
            >
              {isWebflowEnabled ? 'Webflow Page' : 'Created Page'}
            </Button>
            <Button
              size="sm"
              variant={directLinkType === 'link' ? undefined : 'outline'}
              onClick={() => setDirectLinkType('link')}
              colorScheme={directLinkType === 'link' ? 'purple' : 'gray'}
              w="full"
            >
              {isWebflowEnabled ? 'External Link' : 'Link'}
            </Button>
          </ButtonGroup>

          {(directLinkType === 'link' ||
            (isWebflowEnabled && directLinkType === 'created-page')) && (
              <FormControl isInvalid={!!errors.href}>
                <FormLabel>
                  {isWebflowEnabled && directLinkType === 'created-page'
                    ? 'Webflow path'
                    : 'Link'}
                </FormLabel>
                <Input
                  {...register('href', {
                    validate: {
                      requiredIf: (value) => {
                        if (isWebflowEnabled) {
                          return !!value || 'Link is required';
                        }
                        return (
                          getValues('directLinkType') === 'created-page' ||
                          !!value ||
                          'Link is required'
                        );
                      },
                      webflowPath: (value) => {
                        if (
                          !isWebflowEnabled ||
                          getValues('directLinkType') !== 'created-page' ||
                          !value
                        ) {
                          return true;
                        }
                        const trimmed = value.trim();
                        if (isExternalHttpUrl(trimmed)) {
                          return 'Use External Link for full website URLs';
                        }
                        return (
                          trimmed.startsWith('/') ||
                          'Enter a site path starting with / (e.g. /site/about)'
                        );
                      },
                      externalUrl: (value) => {
                        if (
                          !isWebflowEnabled ||
                          getValues('directLinkType') !== 'link' ||
                          !value
                        ) {
                          return true;
                        }
                        const trimmed = value.trim();
                        if (trimmed.startsWith('/')) {
                          return 'Use Webflow Page for on-site paths like /site/about';
                        }
                        return (
                          isExternalHttpUrl(trimmed) ||
                          'Enter a full URL starting with https://'
                        );
                      },
                    },
                  })}
                  placeholder={
                    isWebflowEnabled && directLinkType === 'created-page'
                      ? '/site/about'
                      : isWebflowEnabled
                        ? 'https://example.com'
                        : '/site/about'
                  }
                />
                {isWebflowEnabled && directLinkType === 'created-page' && (
                  <FormHelperText>
                    Path of the published Webflow page on this site (usually{' '}
                    <Semibold>/site/…</Semibold>).
                  </FormHelperText>
                )}
                <FormErrorMessage>{errors.href?.message}</FormErrorMessage>
              </FormControl>
            )}

          {directLinkType === 'created-page' && !isWebflowEnabled && (
            <FormControl isInvalid={!!errors.page}>
              <FormLabel>Link to</FormLabel>
              <Controller
                name="page"
                control={control}
                rules={{
                  validate: {
                    requiredIf: (value) =>
                      getValues('directLinkType') === 'link' ||
                      !!value ||
                      'Link is required',
                  },
                }}
                render={({ field }) => <SearchablePageSelect {...field} />}
              />
              <FormErrorMessage>{errors.page?.message}</FormErrorMessage>
            </FormControl>
          )}

          <FormControl
            isDisabled={directLinkType === 'created-page' && !isWebflowEnabled}
          >
            <FormLabel>Visible to</FormLabel>
            <Select {...register('restriction')}>
              <option value={ENavigationItemRestriction.ANYONE}>
                {
                  READABLE_NAVIGATION_ITEM_RESTRICTION[
                    ENavigationItemRestriction.ANYONE
                  ]
                }
              </option>
              <option value={ENavigationItemRestriction.LOGGED_IN}>
                {
                  READABLE_NAVIGATION_ITEM_RESTRICTION[
                    ENavigationItemRestriction.LOGGED_IN
                  ]
                }
              </option>
              <option value={ENavigationItemRestriction.MEMBERS_ONLY}>
                {
                  READABLE_NAVIGATION_ITEM_RESTRICTION[
                    ENavigationItemRestriction.MEMBERS_ONLY
                  ]
                }
              </option>
              <option value={ENavigationItemRestriction.NOBODY}>
                {
                  READABLE_NAVIGATION_ITEM_RESTRICTION[
                    ENavigationItemRestriction.NOBODY
                  ]
                }
              </option>
            </Select>
            {directLinkType === 'created-page' && page && !isWebflowEnabled && (
              <Text color="gray.500" textStyle="detail" mt="4">
                The page permission is set to{' '}
                <Semibold textTransform="lowercase">
                  {READABLE_PAGE_RESTRICTION[page.restriction]}
                </Semibold>
                . To change that, please{' '}
                <Link
                  as={NextLink}
                  href={`/website-builder/${page._id}?tabIndex=2`}
                  textDecoration="underline"
                >
                  go to the page setting
                </Link>
                .
              </Text>
            )}
          </FormControl>
        </Stack>
      </Card>

      <Button colorScheme="purple" onClick={onSubmit} size="sm">
        {props.submitText}
      </Button>
    </Stack>
  );
};
